import React, { useEffect, useState } from 'react';
import { Package, Download, ExternalLink, FileText, Lock, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import MarketplaceOrdersSection from '../../components/MarketplaceOrdersSection';

const UserOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState({});
  const [tab, setTab] = useState('marketplace'); // Support fix 2026-09-24: marketplace orders tab

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(response.data);
      
      // Fetch product details for completed orders to get download links
      const completedOrders = response.data.filter(o => o.payment_status === 'completed' && o.admin_approved);
      const productIds = [...new Set(completedOrders.flatMap(o => o.items.map(i => i.product_id)))];
      
      const productDetails = {};
      for (const productId of productIds) {
        try {
          const productRes = await api.get(`/products/${productId}`);
          productDetails[productId] = productRes.data;
        } catch (e) {
          // Product might not exist or be a course
          try {
            const courseRes = await api.get(`/courses/${productId}`);
            productDetails[productId] = courseRes.data;
          } catch (e2) {
            // Ignore
          }
        }
      }
      setProducts(productDetails);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const isOrderCompleted = (order) => {
    return order.payment_status === 'completed' && order.admin_approved;
  };

  // Check if user can access product files (must be activated AND order approved)
  const canAccessProduct = (order) => {
    return user?.is_active && isOrderCompleted(order);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="user-orders-page">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="orders-title">My Orders</h1>

        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab('marketplace')} data-testid="orders-tab-marketplace"
                  className={`px-4 py-2 rounded-xl text-sm font-bold ${tab === 'marketplace' ? 'bg-primary text-primary-foreground' : 'bg-white/5 text-muted-foreground'}`}>
            🛍️ Marketplace Orders
          </button>
          <button onClick={() => setTab('webshop')} data-testid="orders-tab-webshop"
                  className={`px-4 py-2 rounded-xl text-sm font-bold ${tab === 'webshop' ? 'bg-primary text-primary-foreground' : 'bg-white/5 text-muted-foreground'}`}>
            📦 Products &amp; Courses ({orders.length})
          </button>
        </div>

        {tab === 'marketplace' ? <MarketplaceOrdersSection /> : orders.length > 0 ? (
          <div className="space-y-6">
            {orders.map((order) => (
              <div key={order.id} className="glass-card p-6" data-testid={`order-${order.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-xl font-bold mb-1">Order #{order.id.slice(0, 8)}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary mb-1">₹{Number(order.total_amount || 0).toFixed(2)}</p>
                    <span className={`text-xs px-3 py-1 rounded-full ${
                      order.payment_status === 'completed'
                        ? 'bg-accent/20 text-accent'
                        : order.payment_status === 'rejected'
                        ? 'bg-red-500/20 text-red-500'
                        : 'bg-yellow-500/20 text-yellow-500'
                    }`}>
                      {order.payment_status === 'completed' ? 'Completed' : 
                       order.payment_status === 'rejected' ? 'Rejected' : 'Pending'}
                    </span>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <p className="text-sm font-semibold mb-3">Items ({order.items.length})</p>
                  <div className="space-y-3">
                    {order.items.map((item, index) => {
                      const productData = products[item.product_id];
                      const hasFiles = productData?.files?.length > 0;
                      const hasLinks = productData?.external_links?.length > 0;
                      
                      return (
                        <div key={index} className="flex justify-between items-center">
                          <div className="flex-1">
                            <span className="text-muted-foreground">{item.name}</span>
                            <span className="ml-2 font-semibold">₹{item.price}</span>
                          </div>
                          
                          {/* Show download/access buttons for completed orders */}
                          {isOrderCompleted(order) && (hasFiles || hasLinks) && (
                            <div className="flex gap-2 ml-4">
                              {canAccessProduct(order) ? (
                                <>
                                  {hasFiles && productData.files.map((file, fileIndex) => (
                                    <a
                                      key={fileIndex}
                                      href={file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-all"
                                      data-testid={`download-${item.product_id}-${fileIndex}`}
                                    >
                                      <Download className="w-4 h-4" />
                                      Download
                                    </a>
                                  ))}
                                  {hasLinks && productData.external_links.map((link, linkIndex) => (
                                    <a
                                      key={linkIndex}
                                      href={link.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 px-3 py-1.5 bg-accent text-accent-foreground rounded-lg text-sm hover:bg-accent/90 transition-all"
                                      data-testid={`access-${item.product_id}-${linkIndex}`}
                                    >
                                      <ExternalLink className="w-4 h-4" />
                                      Access
                                    </a>
                                  ))}
                                </>
                              ) : (
                                <span className="flex items-center gap-1 px-3 py-1.5 bg-gray-500/20 text-gray-400 rounded-lg text-sm cursor-not-allowed">
                                  <Lock className="w-4 h-4" />
                                  Locked
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Show access granted message for completed orders */}
                {isOrderCompleted(order) && canAccessProduct(order) && (
                  <div className="mt-4 p-3 bg-accent/10 rounded-lg border border-accent/20">
                    <div className="flex items-center gap-2 text-accent">
                      <FileText className="w-5 h-5" />
                      <span className="font-semibold">Access Granted!</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Your payment has been approved. Download or access your products above.
                    </p>
                  </div>
                )}

                {/* Show activation required message */}
                {isOrderCompleted(order) && !user?.is_active && (
                  <div className="mt-4 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                    <div className="flex items-center gap-2 text-orange-500">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-semibold">Account Activation Required</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Your order is approved but you need to activate your account to access the products.
                    </p>
                  </div>
                )}

                {/* Show pending message */}
                {order.payment_status === 'pending' && (
                  <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                    <p className="text-sm text-yellow-500">
                      Payment pending approval. Please contact admin with payment proof.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 glass-card" data-testid="no-orders">
            <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-4">No orders yet</h2>
            <p className="text-muted-foreground">Start shopping to see your orders here</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserOrders;
